'use client'

import { useEffect } from 'react'
import { reportClientError } from '@colix/frontend-logger'
import { logger } from '@/lib/logger'

/**
 * Next.js global error boundary — catches errors in the root layout.
 * This is the last line of defense before a blank page.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    reportClientError(
      logger,
      error,
      {
        component: 'GlobalErrorBoundary',
        action: 'root_layout_crash',
        error_digest: error.digest,
        page_url: window.location.href,
        error_category:
          error.message.includes('#418') || error.message.includes('#422')
            ? 'hydration'
            : undefined,
      },
      {
        reporter: 'next-global-boundary',
      }
    )
  }, [error])

  const isGeneric =
    error.message.includes('Server Components') || error.message.includes('Minified React')
  const displayMessage = isGeneric ? 'Something went wrong loading this page.' : error.message

  return (
    <html lang="en">
      <body
        className="antialiased"
        // The root error boundary cannot assume the application stylesheet loaded,
        // so these literals intentionally mirror the Clear tokens in
        // @colix/design-tokens (background, foreground, muted-foreground, accent).
        style={{ background: '#FFFFFF', color: '#182230', fontFamily: "'Inter Variable', Inter, system-ui, sans-serif" }}
      >
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <h1 className="text-2xl font-semibold mb-4">Something went wrong</h1>
            <p className="mb-6 text-sm" style={{ color: '#526071' }}>
              {displayMessage}
            </p>
            {error.digest && (
              <p className="mb-4 font-mono text-xs" style={{ color: '#526071' }}>
                {error.digest}
              </p>
            )}
            <button
              onClick={reset}
              className="inline-flex h-9 items-center justify-center px-3.5 text-sm font-medium"
              style={{ background: '#087F5B', color: '#FFFFFF', borderRadius: 6 }}
            >
              Try again
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}
